"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IntakeForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="intake-form" ref={ref} className={`py-16 lg:py-24 bg-background transition-opacity duration-[var(--duration-medium)] ${isVisible ? "opacity-100 animate-fade-in-up" : "opacity-0"}`}>
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-10">
          <h1 className="text-4xl lg:text-5xl font-display font-bold tracking-tight mb-4">
            Intake Form
          </h1>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            Begin your journey with ABA With Passion Inc. Fill out the form below to help us understand your family's needs.
          </p>
        </div>

        <FormProvider className="w-full" formId="intake_form_get_started" sectionName="intake-form" defaultValues={{
        parent_name: "",
        parent_email: "",
        parent_phone: "",
        child_name: "",
        child_age: "",
        services_interested: [],
        hipaa_acknowledge: false
      }} validationRules={{
        parent_name: {
          required: true,
          minLength: 2
        },
        parent_email: {
          required: true,
          pattern: "email"
        },
        parent_phone: {
          required: true
        },
        child_name: {
          required: true,
          minLength: 2
        },
        child_age: {
          required: true,
          min: 1,
          max: 21
        },
        services_interested: {
          required: true
        },
        hipaa_acknowledge: {
          required: true
        }
      }} submitText="Submit Intake Form" successMessage="Thank you! We've received your intake form. Our team will contact you within 1-2 business days to discuss the next steps." errorMessage="We couldn't submit your form. Please check your information and try again.">
          <IntakeFormContent />
        </FormProvider>
      </div>
    </section>;
}
function IntakeFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="bg-primary-light border border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="size-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-2">Form Submitted Successfully</h3>
              <p className="text-foreground/80 mb-4">{successMessage}</p>
              <Button onClick={resetForm} variant="outline">
                Submit Another Form
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>;
  }
  return <Card className="shadow-soft border-none">
      <CardContent>
        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-display font-semibold">Parent / Guardian Information</h2>
              
              <FormField control={control} name="parent_name" rules={{
              required: "Parent / Guardian Name is required",
              minLength: {
                value: 2,
                message: "Name must be at least 2 characters"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Parent / Guardian Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Jane Doe" {...field} />
                    </FormControl>
                    <FormDescription>
                      First and last name of the parent or legal guardian.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="parent_email" rules={{
              required: "Email Address is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="jane.doe@example.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      We'll use this to send appointment details and follow-up information.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="parent_phone" rules={{
              required: "Phone Number is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="(555) 123-4567" {...field} />
                    </FormControl>
                    <FormDescription>
                      Preferred number for a quick call to discuss your child's needs.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="space-y-4 pt-4 border-t border-border">
              <h2 className="text-2xl font-display font-semibold">Child / Youth Information</h2>
              
              <FormField control={control} name="child_name" rules={{
              required: "Child / Youth Name is required",
              minLength: {
                value: 2,
                message: "Name must be at least 2 characters"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Child / Youth Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Alex Smith" {...field} />
                    </FormControl>
                    <FormDescription>
                      First and last name of the child or adolescent.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="child_age" rules={{
              required: "Child's Age is required",
              min: {
                value: 1,
                message: "Age must be at least 1"
              },
              max: {
                value: 21,
                message: "Age must be 21 or under"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Child's Age</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="8" {...field} />
                    </FormControl>
                    <FormDescription>
                      Age in years. Our services are available for ages 1-21.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="space-y-4 pt-4 border-t border-border">
              <h2 className="text-2xl font-display font-semibold">Services</h2>
              
              <FormField control={control} name="services_interested" rules={{
              required: "Please select at least one service"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Services You Are Interested In</FormLabel>
                    <FormDescription>
                      Select all that apply. You can discuss additional services during the intake call.
                    </FormDescription>
                    <div className="space-y-3 pt-2">
                      {["Applied Behavior Analysis (ABA)", "Parent Training & Coaching", "Behavior Assessment", "BCBA Clinical Supervision"].map((service, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                          <input type="checkbox" id={`service-${i}`} value={service} checked={Array.isArray(field.value) && field.value.includes(service)} onChange={e => {
                    const currentValue = Array.isArray(field.value) ? field.value : [];
                    if (e.target.checked) {
                      field.onChange([...currentValue, service]);
                    } else {
                      field.onChange(currentValue.filter(v => v !== service));
                    }
                  }} className="mt-1 size-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/40" />
                          <label htmlFor={`service-${i}`} className="text-sm leading-relaxed cursor-pointer select-none">
                            {service}
                          </label>
                        </div>)}
                    </div>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="pt-4 border-t border-border">
              <FormField control={control} name="hipaa_acknowledge" rules={{
              required: "You must acknowledge the HIPAA notice to continue"
            }} render={({
              field
            }) => <FormItem>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" id="hipaa_acknowledge" checked={field.value} onChange={e => field.onChange(e.target.checked)} className="mt-1 size-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/40" />
                      <div className="flex-1">
                        <FormLabel htmlFor="hipaa_acknowledge" className="cursor-pointer">
                          HIPAA Notice Confirmation
                        </FormLabel>
                        <FormDescription className="mt-1">
                          I understand that clinical details should be shared by phone or through a secure portal as per HIPAA guidelines.
                        </FormDescription>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <Card className="bg-secondary-light border border-secondary/20 mt-6">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                  <div className="flex-1 text-sm leading-relaxed">
                    <p className="font-semibold mb-1">HIPAA Privacy Notice</p>
                    <p className="text-foreground/80">
                      For your privacy and security, please do not include sensitive medical or diagnostic information in this form. Our team will discuss clinical details with you during a confidential phone call or through our secure patient portal.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {submitError && <Card className="bg-red-50 border border-red-200">
                <CardContent className="pt-6">
                  <p className="text-red-800 text-sm">{submitError}</p>
                </CardContent>
              </Card>}

            <Button type="submit" disabled={isSubmitting} className="w-full btn-primary" size="lg">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Submitting...
                </> : "Submit Intake Form"}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}